/**
 * Button primitive -- Leviathan design system.
 *
 * Variants:
 *   primary    gold (leviathan default)
 *   secondary  glass tier1 surface
 *   ghost      transparent + hover glass
 *   destructive red, unchanged from shadcn convention
 *   link       text underline
 *
 * Sizes: sm / default (md) / lg, plus icon variants.
 *
 * `accent` prop tints the primary variant to a brand pillar.
 * Acceptable values: 'leviathan' | 'ouroboros' | 'apophis' | 'crossCutting'.
 * Defaults to undefined (leviathan gold via --primary).
 *
 * Press animation uses CSS active:scale-[0.97] so no nested element
 * or framer-motion wrapper is needed. This keeps the DOM clean and avoids
 * nested-button issues when Button is used as a render prop target.
 *
 * Do not add em-dashes anywhere.
 */

import type { CSSProperties } from 'react'
import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { pillarAccent, color, type Pillar } from '@/design-system/tokens'

/**
 * Converts a 6-digit hex color to rgba(...) notation.
 * Pure utility -- does NOT import private helpers from design-system/glass.tsx.
 */
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** Builds the layered accent glow shadow from a token hex value. */
function buildAccentShadow(hex: string): string {
  return `0 0 12px 0 ${hexToRgba(hex, 0.18)}, 0 0 32px 0 ${hexToRgba(hex, 0.28)}`
}

const buttonVariants = cva(
  // base
  'group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:scale-[0.97] active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
  {
    variants: {
      variant: {
        // primary -- gold hero variant with token-derived shadow (set via inline style below)
        primary:
          'bg-primary text-primary-foreground hover:bg-primary/80 hover:brightness-105 transition-[background-color,filter,box-shadow]',
        secondary:
          'border-white/10 bg-white/5 text-foreground backdrop-blur-sm hover:bg-white/10 hover:border-white/15',
        ghost:
          'bg-transparent text-foreground/70 hover:bg-white/6 hover:text-foreground',
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20',
        link: 'text-primary underline-offset-4 hover:underline',
        // legacy aliases so existing code using 'default' / 'outline' does not break
        default:
          'bg-primary text-primary-foreground hover:bg-primary/80',
        outline:
          'border-border bg-background hover:bg-muted hover:text-foreground',
      },
      size: {
        default:
          'h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        sm: 'h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*="size-"])]:size-3.5',
        md: 'h-8 gap-1.5 px-2.5',
        lg: 'h-9 gap-1.5 px-3',
        xs: 'h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*="size-"])]:size-3',
        icon: 'size-8',
        'icon-xs':
          'size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*="size-"])]:size-3',
        'icon-sm':
          'size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg',
        'icon-lg': 'size-9',
      },
    },
    defaultVariants: {
      // Reverted from 'primary' back to 'default' so existing call sites that
      // omit `variant` render identically to pre-WS-0b behavior.
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends ButtonPrimitive.Props,
    VariantProps<typeof buttonVariants> {
  accent?: Pillar
}

function accentStyle(
  variant: string | null | undefined,
  accent: Pillar | undefined,
): CSSProperties {
  if (!accent) return {}
  const hex = pillarAccent[accent]
  // Only tint primary/default variants. Others stay token-agnostic.
  if (variant === 'primary' || variant === 'default' || !variant) {
    return {
      backgroundColor: hex,
      color: color.blackRich,
      boxShadow: buildAccentShadow(hex),
    } as CSSProperties
  }
  if (variant === 'secondary') {
    return { borderColor: `${hex}25`, color: hex } as CSSProperties
  }
  return {}
}

/**
 * Returns inline shadow style for primary variant (token-derived, zero raw hex).
 * For non-primary variants returns undefined so no inline style is set.
 */
function primaryShadowStyle(
  variant: string | null | undefined,
  accent: Pillar | undefined,
): CSSProperties {
  if (variant !== 'primary') return {}
  // If accent is set, accentStyle already adds boxShadow via accent hex.
  if (accent) return {}
  return { boxShadow: buildAccentShadow(color.gold) }
}

function Button({
  className,
  variant = 'default',
  size = 'default',
  accent,
  style,
  ...props
}: ButtonProps) {
  const extraStyle = accentStyle(variant, accent)
  const shadowStyle = primaryShadowStyle(variant, accent)
  return (
    <ButtonPrimitive
      data-slot="button"
      data-accent={accent}
      className={cn(buttonVariants({ variant, size, className }))}
      style={{ ...shadowStyle, ...extraStyle, ...style }}
      {...props}
    />
  )
}

export { Button, buttonVariants }
